import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Logo from '../components/Logo';

const FAQ_ITEMS = [
	{
		q: 'How is iVault different from sharing a Google Drive folder?',
		a: "Google Drive gives you file storage — iVault gives you control, intelligence, and security. With iVault you know exactly who viewed which document and for how long, you can revoke access in under 60 seconds, watermark every page, gate entry behind an NDA, and manage investor Q&A threads without messy email chains. No investor has ever signed a term sheet impressed by a Google Drive link.",
	},
	{
		q: 'Can I have multiple data rooms for different investors or rounds?',
		a: 'Yes. On the Growth plan you can run up to 5 simultaneous data rooms — perfect for running parallel conversations with lead investors, angels, and strategics, or keeping a pre-seed room separate from your Series A. Enterprise plans come with unlimited rooms.',
	},
	{
		q: 'Who is iVault for — startups, investors, or both?',
		a: 'iVault is built for startups and founders raising capital. You create data rooms, share pitch decks and due diligence materials, and manage NDAs and access in one place. Investors receive secure links and view documents without needing an account. Accelerators, VC firms, and investor networks can also use iVault to organize deal flow and share materials with their team.',
	},
	{
		q: 'Can investors download documents, or is viewing only?',
		a: 'You decide, per file. You can allow full download, viewing only (with our secure in-browser renderer), or printing only — all per document or folder. You can also apply dynamic watermarks with the investor\'s name and email to any downloaded file.',
	},
	{
		q: 'How does the NDA gating work?',
		a: 'When you enable NDA gating, any investor who receives your room link will be required to read and e-sign your custom NDA before seeing a single document. Signatures are legally binding and timestamped. You can use our standard NDA template or upload your own.',
	},
	{
		q: 'Do investors need to create an iVault account?',
		a: 'No. Investors access your data room via a secure link — no account required on their end. They authenticate via email verification or magic link, sign any required NDA, and get immediate access to the documents you\'ve shared.',
	},
];

const marqueeItems = [
	'Investors — free login, activity fee when linked',
	'Startup subscriptions & storage plans',
	'Investor networks & link limits (50+ investors)',
	'Storage & usage calculations',
	'Data rooms & secure document storage',
	'NDA management & e-signature',
	'Granular access control',
	'Real-time investor analytics',
	'Pitch events & deal flow',
	'Subscription & billing (annual & add-ons)',
	'Audit logs & compliance',
	'Investment & portfolio tracking',
	'Multiple data rooms per raise',
];

const FEATURE_ICONS = {
	access: (
		<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8">
			<rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
			<path d="M7 11V7a5 5 0 0 1 10 0v4" />
		</svg>
	),
	realtime: (
		<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8">
			<path d="M4 12a8 8 0 0 1 16 0" />
			<path d="M4 16a8 8 0 0 1 16 0" />
			<path d="M4 8a8 8 0 0 1 16 0" />
		</svg>
	),
	esign: (
		<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8">
			<path d="M12 19l7-7 3 3-7 7-3-3z" />
			<path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z" />
		</svg>
	),
	qa: (
		<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8">
			<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
		</svg>
	),
	organise: (
		<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8">
			<path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
		</svg>
	),
	syndication: (
		<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8">
			<circle cx="18" cy="5" r="3" />
			<circle cx="6" cy="12" r="3" />
			<circle cx="18" cy="19" r="3" />
			<path d="M8.59 13.51l6.83 3.98" />
			<path d="M15.41 6.51l-6.82 3.98" />
		</svg>
	),
};

const LandingPage = () => {
	const [navShadow, setNavShadow] = useState(false);
	const [openFaq, setOpenFaq] = useState(null);
	const [videoModalOpen, setVideoModalOpen] = useState(false);
	const [visible, setVisible] = useState({});

	useEffect(() => {
		const onScroll = () => setNavShadow(window.scrollY > 40);
		window.addEventListener('scroll', onScroll);
		return () => window.removeEventListener('scroll', onScroll);
	}, []);

	useEffect(() => {
		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((e) => {
					if (e.isIntersecting && e.target.id) setVisible((prev) => ({ ...prev, [e.target.id]: true }));
				});
			},
			{ threshold: 0.1 }
		);
		document.querySelectorAll('[data-reveal]').forEach((el) => observer.observe(el));
		return () => observer.disconnect();
	}, []);

	useEffect(() => {
		if (videoModalOpen) document.body.style.overflow = 'hidden';
		else document.body.style.overflow = '';
		return () => { document.body.style.overflow = ''; };
	}, [videoModalOpen]);

	useEffect(() => {
		const onEscape = (e) => { if (e.key === 'Escape') setVideoModalOpen(false); };
		document.addEventListener('keydown', onEscape);
		return () => document.removeEventListener('keydown', onEscape);
	}, []);

	const toggleFaq = (i) => setOpenFaq((prev) => (prev === i ? null : i));

	return (
		<div className="min-h-screen bg-paper text-ink font-sans antialiased">
			{/* Nav */}
			<nav
				className="fixed top-0 left-0 right-0 z-[100] flex items-center justify-between px-6 md:px-12 py-4 bg-paper/92 backdrop-blur-md border-b border-[var(--rule)] transition-shadow"
				style={{ boxShadow: navShadow ? '0 4px 24px rgba(13,13,13,0.08)' : 'none' }}
			>
				<Link to="/">
					<Logo showText={true} width={40} height={40} />
				</Link>
				<ul className="hidden md:flex items-center gap-8 list-none">
					<li><a href="#features" className="font-mono text-[11px] tracking-widest uppercase text-muted hover:text-ink no-underline">Features</a></li>
					<li><a href="#roles" className="font-mono text-[11px] tracking-widest uppercase text-muted hover:text-ink no-underline">For you</a></li>
					<li><a href="#demo" className="font-mono text-[11px] tracking-widest uppercase text-muted hover:text-ink no-underline">Demo</a></li>
					<li><a href="#pricing" className="font-mono text-[11px] tracking-widest uppercase text-muted hover:text-ink no-underline">Pricing</a></li>
					<li><a href="#faq" className="font-mono text-[11px] tracking-widest uppercase text-muted hover:text-ink no-underline">FAQ</a></li>
				</ul>
				<div className="flex items-center gap-3">
					<Link
						to="/login"
						className="font-mono text-[11px] font-medium tracking-widest uppercase text-ink hover:text-gold no-underline transition-colors"
					>
						Sign In
					</Link>
					<Link
						to="/signup"
						className="font-mono text-[11px] font-semibold tracking-[0.16em] uppercase text-paper bg-gradient-to-r from-gold to-gold-light py-2.5 px-7 rounded-full no-underline shadow-sm hover:from-gold-light hover:to-gold hover:shadow-md transition-all"
					>
						Sign up
					</Link>
				</div>
			</nav>

			{/* Hero */}
			<section className="min-h-screen grid grid-cols-1 lg:grid-cols-2 pt-[74px] overflow-hidden">
				<div className="flex flex-col justify-center px-8 md:px-12 lg:pl-16 lg:pr-12 py-16 lg:py-20 relative z-10">
					<div className="font-mono text-[10px] font-medium tracking-[0.18em] uppercase text-gold mb-6 flex items-center gap-3 animate-[fadeUp_0.8s_ease_0.2s_forwards] opacity-0">
						<span className="w-8 h-px bg-gold" />
						Institutional-Grade Data Room
					</div>
					<h1 className="font-serif text-[clamp(50px,5.8vw,86px)] font-light leading-[1.02] tracking-tight text-ink mb-7 animate-[fadeUp_0.9s_ease_0.35s_forwards] opacity-0">
						Close rounds
						<br />
						with <em className="italic text-gold">confidence</em>
					</h1>
					<p className="text-[17px] text-muted max-w-[420px] mb-12 leading-[1.75] animate-[fadeUp_0.9s_ease_0.5s_forwards] opacity-0">
						The secure, intelligent data room built for startup fundraising — from pre-seed to Series C. Share documents, manage investor access, and track engagement in real time.
					</p>
					<div className="flex flex-wrap items-center gap-4 animate-[fadeUp_0.9s_ease_0.65s_forwards] opacity-0">
						<Link to="/signup" className="font-mono text-[11px] font-medium tracking-widest uppercase text-paper bg-ink border-[1.5px] border-ink py-3.5 px-7 no-underline hover:bg-gold hover:border-gold hover:-translate-y-0.5 transition-all inline-block">
							Start now
						</Link>
						<Link to="/login" className="font-mono text-[11px] font-medium tracking-widest uppercase text-ink bg-transparent border border-[var(--rule)] py-3.5 px-7 no-underline hover:border-ink hover:-translate-y-0.5 transition-all inline-block">
							Sign in
						</Link>
					</div>
					<div className="mt-10 pt-8 border-t border-[var(--rule)] animate-[fadeUp_0.9s_ease_0.8s_forwards] opacity-0">
						<div className="font-mono text-[10px] tracking-[0.18em] uppercase text-gold mb-3">
							Frequently asked questions
						</div>
						<div className="space-y-2">
							<a href="#faq" className="block font-sans text-sm text-ink hover:text-gold transition-colors">
								How is iVault different from a Google Drive folder?
							</a>
							<a href="#faq" className="block font-sans text-sm text-ink hover:text-gold transition-colors">
								Can I run multiple data rooms at once?
							</a>
							<a href="#faq" className="block font-sans text-sm text-ink hover:text-gold transition-colors">
								Who is iVault for?
							</a>
						</div>
					</div>
				</div>
				<div className="relative flex items-center justify-center bg-cream border-l border-[var(--rule)] overflow-hidden animate-[fadeIn_1.2s_ease_0.4s_forwards] opacity-0 p-6 lg:p-8">
					<div className="absolute top-[-100px] right-[-60px] w-[360px] h-[360px] bg-[radial-gradient(circle,rgba(184,147,58,0.13)_0%,transparent_70%)] pointer-events-none" />
					<div className="w-full max-w-[90%] rounded-lg overflow-hidden shadow-[0_32px_80px_rgba(13,13,13,0.14),0_8px_24px_rgba(13,13,13,0.06)] border border-black/10 animate-[float_6s_ease-in-out_infinite_1.5s]" style={{ transform: 'perspective(800px) rotateY(-2deg) rotateX(1deg)' }}>
						<img
							src="/dashboard-preview.png"
							alt="iVault Dashboard — Dashboard Overview, Quick Actions, and navigation"
							className="w-full h-auto block object-cover object-top"
						/>
					</div>
				</div>
			</section>

			{/* Marquee — list repeated once for seamless loop (no consecutive duplicates) */}
			<div className="border-t border-b border-[var(--rule)] bg-cream py-4 overflow-hidden">
				<div className="flex gap-12 animate-[marquee_30s_linear_infinite] whitespace-nowrap">
					{[...marqueeItems, ...marqueeItems].map((item, i) => (
						<span key={i} className="font-mono text-[11px] tracking-[0.14em] uppercase text-muted flex items-center gap-12 flex-shrink-0">
							{item}
							<span className="text-[7px] text-gold">◆</span>
						</span>
					))}
				</div>
			</div>

			{/* Features */}
			<section id="features" className="py-24 px-6 md:px-16 lg:px-20">
				<div data-reveal id="feat-label" className={`font-mono text-[10px] font-medium tracking-[0.18em] uppercase text-gold mb-4 flex items-center gap-3 transition-all duration-700 ${visible['feat-label'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
					<span className="w-6 h-px bg-gold" />
					Platform Features
				</div>
				<h2 data-reveal id="feat-h2" className={`font-serif text-[clamp(34px,3.8vw,56px)] font-light leading-[1.1] tracking-tight text-ink mb-14 max-w-[560px] transition-all duration-700 delay-100 ${visible['feat-h2'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
					Everything you need
					<br />
					to <em className="italic text-gold">close the round</em>
				</h2>
				<div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[var(--rule)] border border-[var(--rule)]">
					{[
						{ num: '01', icon: 'access', title: 'Granular Access Control', p: 'Assign permissions by file, folder, or investor group. Revoke access instantly. Auto-expire links. Every touchpoint, your rules.' },
						{ num: '02', icon: 'realtime', title: 'Real-Time Engagement', p: 'See exactly who viewed what, for how long, and how many times. Know which investors are serious before the follow-up call.' },
						{ num: '03', icon: 'esign', title: 'Integrated NDA & E-Sign', p: 'Gate your room behind a custom NDA. Collect legally binding signatures before any document is revealed.' },
						{ num: '04', icon: 'qa', title: 'Q&A Thread Management', p: 'Investors ask questions directly in the room. Route to the right person, answer once, publish to all — or keep private.' },
						{ num: '05', icon: 'organise', title: 'Smart Organisation', p: 'Drag-and-drop folders, version control, bulk upload, and AI-assisted labelling keep your room investor-ready at all times.' },
						{ num: '06', icon: 'syndication', title: 'VC Network Syndication', p: 'Share curated deal rooms with entire angel networks or VC funds in one click. Co-invest, forward, and manage term sheets together.' },
					].map((f, i) => (
						<div key={i} data-reveal id={`feat-${i}`} className={`bg-paper p-8 md:p-9 relative overflow-hidden hover:bg-cream transition-colors group border-b-2 border-transparent hover:border-gold transition-all duration-700 ${visible[`feat-${i}`] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`} style={{ transitionDelay: `${(i % 3) * 120}ms` }}>
							<div className="font-serif text-[52px] font-light text-ink/25 leading-none mb-3">{f.num}</div>
							<div className="w-14 h-14 border-[1.5px] border-[var(--rule)] flex items-center justify-center text-ink group-hover:border-gold group-hover:text-gold transition-colors mb-4">{FEATURE_ICONS[f.icon]}</div>
							<h3 className="font-serif text-[21px] font-normal leading-snug text-ink mb-2">{f.title}</h3>
							<p className="font-sans text-sm leading-[1.7] text-muted">{f.p}</p>
						</div>
					))}
				</div>
			</section>

			{/* Roles */}
			<section id="roles" className="bg-ink py-24 px-6 md:px-16 lg:px-20 relative overflow-hidden">
				<div className="absolute top-[-200px] right-[-100px] w-[700px] h-[700px] bg-[radial-gradient(circle,rgba(184,147,58,0.07)_0%,transparent_65%)] pointer-events-none" />
				<div data-reveal id="roles-label" className={`font-mono text-[10px] font-medium tracking-[0.18em] uppercase text-gold-light mb-4 flex items-center gap-3 transition-all duration-700 ${visible['roles-label'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
					<span className="w-6 h-px bg-gold-light" />
					Built for everyone at the table
				</div>
				<h2 data-reveal id="roles-h2" className={`font-serif text-[clamp(42px,5.5vw,72px)] font-light leading-[1.15] text-paper mb-16 max-w-full transition-all duration-700 delay-100 ${visible['roles-h2'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
					One platform.
					<br />
					<em className="italic text-gold">Every stakeholder.</em>
				</h2>
				<div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-paper/10 relative z-10">
					{[
						{ chip: 'Founders', title: 'Raise on your terms', p: 'Set up your data room in under 10 minutes. Control every document, every permission, every impression you make.', features: ['Branded investor portal', 'One-click NDA gating', 'View & download analytics', 'Multiple rooms per raise', 'Cap table & KPI templates'] },
						{ chip: 'Angel Investors', title: 'Evaluate with clarity', p: 'Access clean, organised deal rooms. Ask questions, annotate docs, and track your pipeline — all in one place.', features: ['Unified deal pipeline view', 'Document annotations', 'Secure messaging with founders', 'Saved searches & alerts', 'Co-invest collaboration'] },
						{ chip: 'VC & Angel Networks', title: 'Manage deal flow at scale', p: 'Coordinate your partners, manage portfolios, and streamline due diligence across your entire fund network.', features: ['Fund-level access management', 'Partner permission tiers', 'Portfolio room templates', 'Audit trails & compliance logs', 'API & CRM integrations'] },
					].map((r, i) => (
						<div key={i} data-reveal id={`role-${i}`} className={`bg-ink p-10 md:p-12 border border-paper/10 hover:border-gold/40 transition-colors duration-200 relative z-10 ${visible[`role-${i}`] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`} style={{ transitionDelay: `${i * 120}ms` }}>
							<span className="inline-block font-mono text-[9px] tracking-[0.14em] uppercase text-gold border border-gold/40 py-1 px-3 mb-5">{r.chip}</span>
							<h3 className="font-serif text-[26px] font-light text-paper leading-tight mb-3">{r.title}</h3>
							<p className="font-sans text-sm leading-[1.7] text-paper/50 mb-6">{r.p}</p>
							<ul className="list-none border-t border-paper/10 pt-4">
								{r.features.map((feat, j) => (
									<li key={j} className="font-mono text-sm text-paper/85 py-2 border-b border-paper/5 flex items-center gap-2">
										<span className="text-gold flex-shrink-0">→</span>
										{feat}
									</li>
								))}
							</ul>
						</div>
					))}
				</div>
			</section>

			{/* Demo */}
			<section id="demo" className="py-24 px-6 md:px-16 lg:px-20 bg-paper border-t border-[var(--rule)] relative overflow-hidden">
				<div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%,rgba(184,147,58,0.06)_0%,transparent_60%)] pointer-events-none" />
				<div className="grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-12 lg:gap-20 items-center relative z-10">
					<div>
						<div data-reveal id="demo-label" className={`font-mono text-[10px] font-medium tracking-[0.18em] uppercase text-gold mb-4 flex items-center gap-3 transition-all duration-700 ${visible['demo-label'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
							<span className="w-6 h-px bg-gold" />
							See it in action
						</div>
						<h2 data-reveal id="demo-h2" className={`font-serif text-[clamp(34px,3.8vw,56px)] font-light leading-[1.1] text-ink mb-5 transition-all duration-700 delay-100 ${visible['demo-h2'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
							Watch iVault close
							<br />
							<em className="italic text-gold">a real deal</em>
						</h2>
						<p data-reveal id="demo-p" className={`text-base text-muted leading-relaxed mb-9 transition-all duration-700 delay-200 ${visible['demo-p'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
							From uploading your first document to the moment an investor signs — see the entire iVault workflow in under 4 minutes.
						</p>
						<ul className="list-none mb-10">
							{['Setting up your branded data room', 'Configuring NDA gates & permissions', 'Inviting investors & tracking engagement', 'Managing Q&A threads live', 'Receiving and countersigning a term sheet'].map((item, i) => (
								<li key={i} className="font-sans text-sm text-ink py-2 border-b border-[var(--rule)] flex items-center gap-3">
									<span className="w-5 h-5 bg-gold flex items-center justify-center text-[11px] text-paper flex-shrink-0">✓</span>
									{item}
								</li>
							))}
						</ul>
						<Link to="/signup" className="font-mono text-[11px] font-medium tracking-widest uppercase text-paper bg-ink border-[1.5px] border-ink py-3.5 px-7 no-underline inline-block hover:bg-gold hover:border-gold hover:-translate-y-0.5 transition-all">
							Sign up
						</Link>
					</div>
					<div
						className="relative bg-ink border border-black/20 shadow-[0_40px_100px_rgba(13,13,13,0.18)] overflow-hidden cursor-pointer aspect-video"
						onClick={() => setVideoModalOpen(true)}
					>
						<div className="absolute inset-0 bg-gradient-to-br from-gold/10 to-transparent" />
						<div className="absolute top-[14%] left-[7.5%] w-[85%] bg-paper/5 border border-paper/10 p-3 font-mono text-[9px] text-paper/30 tracking-wide">
							<div className="flex gap-2 items-center mb-1.5"><span className="w-1.5 h-1.5 rounded-full bg-gold flex-shrink-0" /><span>iVault · Series A Room Setup</span></div>
							<div className="flex gap-2 items-center mb-1.5"><div className="flex-1 h-1 bg-paper/10"><div className="h-full w-[80%] bg-gold/40" /></div><span>Documents 8/10</span></div>
							<div className="flex gap-2 items-center"><div className="flex-1 h-1 bg-paper/10"><div className="h-full w-[60%] bg-gold/40" /></div><span>Investors 3/5</span></div>
						</div>
						<div className="absolute inset-0 flex flex-col items-center justify-center gap-5">
							<div className="w-[72px] h-[72px] rounded-full bg-gold border-[3px] border-paper/15 flex items-center justify-center hover:bg-gold-light hover:scale-105 transition-all shadow-[0_0_0_0_rgba(184,147,58,0.4)] animate-[pulse-gold_3s_ease-in-out_infinite]">
								<div className="w-0 h-0 border-l-[26px] border-l-paper border-t-[16px] border-t-transparent border-b-[16px] border-b-transparent ml-1.5" />
							</div>
							<span className="font-mono text-[10px] tracking-[0.14em] uppercase text-paper/45">Watch the full walkthrough</span>
						</div>
						<div className="absolute top-3 right-3 font-mono text-[10px] tracking-wide bg-black/60 text-gold-light py-1 px-2.5 border border-gold/30">
							03:58
						</div>
						<div className="absolute bottom-0 left-0 right-0 bg-paper/5 py-2.5 px-4 flex items-center gap-3 border-t border-paper/10">
							<span className="text-sm text-paper/40">▶</span>
							<div className="flex-1 h-0.5 bg-paper/10"><div className="h-full w-[35%] bg-gold" /></div>
							<span className="font-mono text-[9px] text-paper/35">0:00 / 3:58</span>
						</div>
					</div>
				</div>
			</section>

			{/* Video Modal */}
			{videoModalOpen && (
				<div
					className="fixed inset-0 bg-ink/90 z-[200] flex items-center justify-center backdrop-blur-sm"
					onClick={(e) => e.target === e.currentTarget && setVideoModalOpen(false)}
				>
					<div className="w-[90%] max-w-[900px] bg-ink border border-paper/10 shadow-2xl relative">
						<button type="button" className="absolute -top-10 right-0 font-mono text-[11px] tracking-wide text-paper/50 hover:text-paper bg-transparent border-none py-2 uppercase" onClick={() => setVideoModalOpen(false)}>
							✕ Close
						</button>
						<div className="w-full aspect-video bg-black flex items-center justify-center font-serif text-2xl italic text-paper/20">
							Your product demo video would appear here
						</div>
					</div>
				</div>
			)}

			{/* Security */}
			<section id="security" className="py-24 px-6 md:px-16 lg:px-20 grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20 items-center border-t border-[var(--rule)]">
				<div>
					<div data-reveal id="sec-label" className={`font-mono text-[10px] font-medium tracking-[0.18em] uppercase text-gold mb-4 flex items-center gap-3 transition-all duration-700 ${visible['sec-label'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
						<span className="w-6 h-px bg-gold" />
						Enterprise Security
					</div>
					<h2 data-reveal id="sec-h2" className={`font-serif text-[clamp(34px,3.8vw,56px)] font-light leading-[1.1] text-ink mb-6 transition-all duration-700 delay-100 ${visible['sec-h2'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
						Your documents,
						<br />
						<em className="italic text-gold">fortress-grade</em>
						<br />
						protection
					</h2>
					<p data-reveal id="sec-p" className={`text-base text-muted leading-relaxed mb-9 transition-all duration-700 delay-200 ${visible['sec-p'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
						When billions are on the line, security isn't optional. iVault was built from the ground up with a strong focus on security and reliability for your data rooms and documents.
					</p>
					<div data-reveal id="sec-badges" className={`flex flex-wrap gap-2 transition-all duration-700 delay-300 ${visible['sec-badges'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
						{['Granular permissions', 'Watermarking & traceability', 'NDA before access', 'Secure share links'].map((badge) => (
							<div key={badge} className="font-mono text-[10px] tracking-wide uppercase text-ink border border-[var(--rule)] py-2 px-4 flex items-center gap-2 hover:border-gold transition-colors">
								<span className="w-1.5 h-1.5 rounded-full bg-gold flex-shrink-0" />
								{badge}
							</div>
						))}
					</div>
				</div>
				<div className="bg-cream border border-[var(--rule)] p-10 relative">
					<span className="absolute -top-px left-8 font-mono text-[9px] tracking-[0.12em] bg-cream px-2 -translate-y-1/2 text-gold">&quot;VAULT&quot;</span>
					<div className="grid grid-cols-2 gap-6">
						{[
							{ val: 'File permissions', label: 'Per file, per folder — control who sees what' },
							{ val: 'All in one place', label: 'Data rooms, NDAs, access & revoke' },
						].map((m) => (
							<div key={m.label} className="p-5 bg-paper border border-[var(--rule)]">
								<div className="font-serif text-3xl font-semibold text-ink leading-none mb-1.5">{m.val}</div>
								<div className="font-mono text-[9px] tracking-wide uppercase text-muted">{m.label}</div>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* Pricing */}
			<section id="pricing" className="py-24 px-6 md:px-16 lg:px-20">
				<div data-reveal id="price-label" className={`font-mono text-[10px] font-medium tracking-[0.18em] uppercase text-gold mb-4 flex items-center gap-3 transition-all duration-700 ${visible['price-label'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
					<span className="w-6 h-px bg-gold" />
					Pricing
				</div>
				<h2 data-reveal id="price-h2" className={`font-serif text-[clamp(34px,3.8vw,56px)] font-light leading-[1.1] text-ink mb-14 transition-all duration-700 delay-100 ${visible['price-h2'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
					Simple, transparent
					<br />
					<em className="italic text-gold">plans that scale</em>
				</h2>
				<div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[var(--rule)] border border-[var(--rule)] mt-14 max-w-4xl mx-auto">
					{[
						{ name: 'Startup Base', userType: 'STARTUP', price: '₹3,000', period: 'per year', storage: '10 GB', investorLinks: null, storageAddon: '+10 GB @ ₹1,000', linkAddon: null, cta: 'Start now' },
						{ name: 'Investor Network', userType: 'INVESTOR_NETWORK', price: '₹5,000', period: 'per year', storage: '10 GB', investorLinks: 50, storageAddon: '+10 GB @ ₹1,000', linkAddon: '+10 investors @ ₹1,000', cta: 'Start now' },
					].map((plan, i) => (
						<div key={i} data-reveal id={`price-${i}`} className={`p-9 md:p-11 relative transition-all duration-700 ${plan.featured ? 'bg-ink' : 'bg-paper hover:bg-cream'} ${visible[`price-${i}`] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`} style={{ transitionDelay: `${i * 120}ms` }}>
							{plan.badge && <div className="absolute -top-px left-1/2 -translate-x-1/2 font-mono text-[9px] tracking-[0.12em] uppercase bg-gold text-paper py-1 px-3.5">{plan.badge}</div>}
							<div className={`font-mono text-[11px] tracking-[0.14em] uppercase mb-4 ${plan.featured ? 'text-paper/40' : 'text-muted'}`}>{plan.name}</div>
							<div className={`font-serif text-5xl font-light leading-none mb-1 ${plan.featured ? 'text-paper' : 'text-ink'}`}>{plan.price}</div>
							<div className={`font-mono text-[11px] mb-6 ${plan.featured ? 'text-paper/40' : 'text-muted'}`}>{plan.period}</div>
							<ul className="list-none mb-8">
								<li className={`font-sans text-[13px] py-2 border-b flex items-center gap-2 ${plan.featured ? 'text-paper/50 border-paper/10' : 'text-muted border-[var(--rule)]'}`}>
									<span className="text-gold text-[11px] flex-shrink-0">✓</span>
									<strong>Storage:</strong> {plan.storage}
								</li>
								{plan.investorLinks != null && (
									<li className={`font-sans text-[13px] py-2 border-b flex items-center gap-2 ${plan.featured ? 'text-paper/50 border-paper/10' : 'text-muted border-[var(--rule)]'}`}>
										<span className="text-gold text-[11px] flex-shrink-0">✓</span>
										<strong>Investor links:</strong> {plan.investorLinks}
									</li>
								)}
								<li className={`font-sans text-[13px] py-2 border-b flex items-center gap-2 ${plan.featured ? 'text-paper/50 border-paper/10' : 'text-muted border-[var(--rule)]'}`}>
									<span className="text-gold text-[11px] flex-shrink-0">✓</span>
									<strong>Storage addon:</strong> {plan.storageAddon}
								</li>
								{plan.linkAddon && (
									<li className={`font-sans text-[13px] py-2 border-b flex items-center gap-2 ${plan.featured ? 'text-paper/50 border-paper/10' : 'text-muted border-[var(--rule)]'}`}>
										<span className="text-gold text-[11px] flex-shrink-0">✓</span>
										<strong>Link addon:</strong> {plan.linkAddon}
									</li>
								)}
							</ul>
							<Link
								to="/signup"
								className={`font-mono text-[11px] font-medium tracking-widest uppercase py-3.5 px-7 w-full block text-center no-underline transition-all ${plan.featured ? 'border border-gold bg-gold text-paper hover:bg-gold-light hover:border-gold-light' : 'border-[1.5px] border-ink text-ink hover:bg-ink hover:text-paper'}`}
							>
								{plan.cta}
							</Link>
						</div>
					))}
				</div>
			</section>

			{/* FAQ */}
			<section id="faq" className="py-24 px-6 md:px-16 lg:px-20 bg-cream border-t border-[var(--rule)]">
				<div className="grid grid-cols-1 lg:grid-cols-[1fr_1.6fr] gap-16 lg:gap-24 items-start">
					<div>
						<div data-reveal id="faq-label" className={`font-mono text-[10px] font-medium tracking-[0.18em] uppercase text-gold mb-4 flex items-center gap-3 transition-all duration-700 ${visible['faq-label'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
							<span className="w-6 h-px bg-gold" />
							FAQ
						</div>
						<h2 data-reveal id="faq-h2" className={`font-serif text-[clamp(34px,3.8vw,56px)] font-light leading-[1.1] text-ink mb-5 transition-all duration-700 delay-100 ${visible['faq-h2'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
							Questions
							<br />
							<em className="italic text-gold">answered</em>
						</h2>
						<p data-reveal id="faq-p" className={`text-[15px] text-muted leading-relaxed mb-9 transition-all duration-700 delay-200 ${visible['faq-p'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
							Can't find what you're looking for? Our team is happy to help.
						</p>
						<a href="mailto:hello@ivault.io" className="font-mono text-[11px] tracking-wide uppercase text-ink border-[1.5px] border-[var(--rule)] py-3 px-6 no-underline inline-block hover:border-gold hover:text-gold transition-colors">
							Talk to us →
						</a>
					</div>
					<div className="flex flex-col">
						{FAQ_ITEMS.map((item, i) => (
							<div key={i} className="border-t border-[var(--rule)] overflow-hidden">
								<button
									type="button"
									className={`w-full flex items-center justify-between py-5 gap-5 text-left font-serif text-xl font-normal text-ink leading-snug bg-transparent border-none cursor-pointer hover:text-gold transition-colors ${openFaq === i ? 'text-gold' : ''}`}
									onClick={() => toggleFaq(i)}
								>
									{item.q}
									<span className={`w-7 h-7 flex-shrink-0 border-[1.5px] flex items-center justify-center text-lg transition-all duration-300 font-mono font-light ${openFaq === i ? 'border-gold text-gold rotate-45' : 'border-[var(--rule)] text-muted'}`}>+</span>
								</button>
								<div className={`overflow-hidden transition-all duration-400 ease-out ${openFaq === i ? 'max-h-[300px]' : 'max-h-0'}`}>
									<p className="font-sans text-[15px] leading-relaxed text-muted pb-5">{item.a}</p>
								</div>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* CTA */}
			<section className="bg-ink py-28 px-6 md:px-16 lg:px-20 text-center relative overflow-hidden">
				<div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[450px] bg-[radial-gradient(ellipse,rgba(184,147,58,0.07)_0%,transparent_70%)] pointer-events-none" />
				<div className="relative z-10">
					<div className="font-mono text-[10px] tracking-[0.18em] uppercase text-gold mb-5">Get started today</div>
					<h2 className="font-serif text-[clamp(40px,5vw,70px)] font-light text-paper leading-tight mb-4">
						Your next round
						<br />
						starts with <em className="italic text-gold">iVault</em>
					</h2>
					<p className="text-base text-paper/50 mb-10 max-w-[420px] mx-auto">
						Join thousands of founders and investors who close smarter, faster, and with full confidence.
					</p>
					<div className="flex flex-wrap items-center justify-center gap-4 mb-5">
						<Link
							to="/signup"
							className="font-mono text-[11px] font-medium tracking-widest uppercase text-ink bg-gold border-[1.5px] border-gold py-3.5 px-8 no-underline hover:bg-gold-light hover:border-gold-light transition-colors inline-block"
						>
							Sign up free
						</Link>
						<Link
							to="/login"
							className="font-mono text-[11px] font-medium tracking-widest uppercase text-paper border-[1.5px] border-paper/40 py-3.5 px-8 no-underline hover:border-paper hover:text-paper transition-colors inline-block"
						>
							Sign in
						</Link>
					</div>
					<p className="font-mono text-[10px] tracking-wide text-paper/30">Start now · No credit card required · Setup in 10 minutes</p>
				</div>
			</section>

			{/* Footer */}
			<footer className="bg-ink border-t border-paper/10 py-12 px-4 sm:px-6 lg:px-8 text-paper/70">
				<div className="max-w-7xl mx-auto">
					<div className="grid grid-cols-1 md:grid-cols-4 gap-8">
						<div>
							<Logo className="[&_span]:!text-paper [&_span:last-child]:!text-paper/60" showText={true} width={40} height={40} />
							<p className="mt-4 text-sm text-paper/60">
								Your secure digital vault for document management and investor relations.
							</p>
						</div>
						<div>
							<h3 className="text-paper font-semibold mb-4">Product</h3>
							<ul className="space-y-2 text-sm">
								<li><a href="#features" className="text-paper/60 hover:text-paper transition-colors">Features</a></li>
								<li><a href="#benefits" className="text-paper/60 hover:text-paper transition-colors">Benefits</a></li>
								<li><Link to="/login" className="text-paper/60 hover:text-paper transition-colors">Sign In</Link></li>
								<li><Link to="/signup" className="text-paper/60 hover:text-paper transition-colors">Sign up</Link></li>
							</ul>
						</div>
						<div>
							<h3 className="text-paper font-semibold mb-4">Company</h3>
							<ul className="space-y-2 text-sm">
								<li><Link to="/about" className="text-paper/60 hover:text-paper transition-colors">About</Link></li>
								<li><Link to="/blog" className="text-paper/60 hover:text-paper transition-colors">Blog</Link></li>
								<li><Link to="/careers" className="text-paper/60 hover:text-paper transition-colors">Careers</Link></li>
								<li><Link to="/contact" className="text-paper/60 hover:text-paper transition-colors">Contact</Link></li>
							</ul>
						</div>
						<div>
							<h3 className="text-paper font-semibold mb-4">Legal</h3>
							<ul className="space-y-2 text-sm">
								<li><Link to="/privacy" className="text-paper/60 hover:text-paper transition-colors">Privacy Policy</Link></li>
								<li><Link to="/terms" className="text-paper/60 hover:text-paper transition-colors">Terms of Service</Link></li>
							</ul>
						</div>
					</div>
					<div className="mt-8 pt-8 border-t border-paper/10 text-center text-sm text-paper/50">
						<p>© {new Date().getFullYear()} iVault. All rights reserved. Techpuram.</p>
					</div>
				</div>
			</footer>

			<style>{`
				@keyframes fadeUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
				@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
				@keyframes float { 0%, 100% { transform: perspective(800px) rotateY(-4deg) rotateX(2deg) translateY(0); } 50% { transform: perspective(800px) rotateY(-4deg) rotateX(2deg) translateY(-8px); } }
				@keyframes marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
				@keyframes pulse-gold { 0%, 100% { box-shadow: 0 0 0 0 rgba(184,147,58,0); } 50% { box-shadow: 0 0 0 16px rgba(184,147,58,0.15); } }
			`}</style>
		</div>
	);
};

export default LandingPage;
